// Purchase.tsx - 采购订单管理 (重写版 2026-05-08)
// ============================
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, Select, DatePicker, InputNumber, message, Tag, Popconfirm, Row, Col, Divider } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined, EyeOutlined, PayCircleOutlined } from '@ant-design/icons'
import { purchaseApi, supplierApi, warehouseApi, productApi } from '../services/api'
import dayjs from 'dayjs'

const { Option } = Select

// --- 类型定义 ---
interface PurchaseItem {
  id?: number
  product_id: number
  product_name: string
  quantity: number
  unit_price: number
  total_price: number
  received_qty?: number
}

interface SupplierRef { id: number; name: string }
interface WarehouseRef { id: number; name: string }
interface Product { id: number; name: string; pinyin?: string; enterprise_price: number; personal_price?: number; code?: string }
interface Purchase {
  id: number; order_no: string; supplier_id: number; supplier_name?: string; supplier?: SupplierRef;
  warehouse_id: number; warehouse_name?: string; warehouse?: WarehouseRef; total_amount: number;
  status: number; expected_date: string; remark: string; created_at: string; items: PurchaseItem[]
}

// 状态映射
const STATUS_MAP: Record<number, { text: string; color: string }> = {
  1: { text: '草稿', color: 'default' },
  2: { text: '已确认', color: 'blue' },
  3: { text: '已入库', color: 'orange' },
  4: { text: '已完成', color: 'green' },
  5: { text: '已取消', color: 'red' }
}

// 安全数组工具（防止 esbuild 优化掉）
const safeArr = <T,>(x: unknown): T[] => (Array.isArray(x) ? x as T[] : [])

// --- 主组件 ---
export default function Purchase() {
  const [loading, setLoading] = useState(false)
  const [list, setList] = useState<Purchase[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 弹窗状态
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [receiveVisible, setReceiveVisible] = useState(false)
  const [auditVisible, setAuditVisible] = useState(false)
  const [payVisible, setPayVisible] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [currentPurchase, setCurrentPurchase] = useState<Purchase | null>(null)
  const [stockInLogs, setStockInLogs] = useState<any[]>([])

  // 下拉数据（初始为空，安全加载）
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([])
  const [warehouses, setWarehouses] = useState<Array<{ id: number; name: string }>>([])
  const [products, setProducts] = useState<Product[]>([])

  // 新增/编辑表单
  const [items, setItems] = useState<PurchaseItem[]>([])
  const [form] = Form.useForm()
  const [receiveForm] = Form.useForm()
  const [payForm] = Form.useForm()
  const [searchForm] = Form.useForm()

  // 加载下拉数据
  const loadRefData = useCallback(async () => {
    const [s, w, p] = await Promise.allSettled([
      supplierApi.getList(),
      warehouseApi.getList(),
      productApi.getProducts({ page_size: 1000 })
    ])

    if (s.status === 'fulfilled' && s.value.code === 200) {
      setSuppliers(safeArr<{ id: number; name: string }>(s.value.data?.list))
    }
    if (w.status === 'fulfilled' && w.value.code === 200) {
      setWarehouses(safeArr<{ id: number; name: string }>(w.value.data))
    }
    if (p.status === 'fulfilled' && p.value.code === 200) {
      setProducts(safeArr<Product>(p.value.data?.list).map((x: Product) => ({
        ...x,
        enterprise_price: Number(x.enterprise_price) || 0
      })))
    }
  }, [])

  // 加载列表
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const vals = searchForm.getFieldsValue()
      const res = await purchaseApi.getList({ page, page_size: pageSize, ...vals })
      if (res.code === 200) {
        setList(safeArr<Purchase>(res.data?.list))
        setTotal(Number(res.data?.total) || 0)
      }
    } catch {
      message.error('加载采购订单失败')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, searchForm])

  useEffect(() => { loadRefData() }, [loadRefData])
  useEffect(() => { loadData() }, [loadData])

  const handleSearch = () => { setPage(1); loadData() }
  const handleReset = () => { searchForm.resetFields(); setPage(1); loadData() }

  // ---- 新增 ----
  const handleCreate = () => {
    setIsEdit(false)
    setCurrentPurchase(null)
    form.resetFields()
    setItems([])
    setModalVisible(true)
  }

  // ---- 编辑 ----
  const handleEdit = async (record: Purchase) => {
    setIsEdit(true)
    setCurrentPurchase(record)
    try {
      const res = await purchaseApi.get(record.id)
      if (res.code === 200) {
        const d = res.data
        form.setFieldsValue({
          supplier_id: d.supplier_id,
          warehouse_id: d.warehouse_id,
          expected_date: d.expected_date ? dayjs(d.expected_date) : null,
          remark: d.remark
        })
        setItems(safeArr<PurchaseItem>(d.items).map((item: any) => ({
          id: item.id,
          product_id: item.product_id,
          product_name: item.product?.name || item.product_name || '',
          quantity: Number(item.quantity) || 1,
          unit_price: Number(item.unit_price) || 0,
          total_price: Number(item.total_price) || 0,
          received_qty: item.received_qty
        })))
        setModalVisible(true)
      }
    } catch { message.error('加载详情失败') }
  }

  // ---- 提交 ----
  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields()
      if (items.length === 0) { message.error('请至少添加一个商品'); return }
      const payload = {
        ...vals,
        expected_date: vals.expected_date?.format('YYYY-MM-DD'),
        items: items.map(it => ({ product_id: it.product_id, quantity: it.quantity, unit_price: it.unit_price }))
      }
      const res = isEdit && currentPurchase
        ? await purchaseApi.update(currentPurchase.id, payload)
        : await purchaseApi.create(payload)
      if (res.code === 200) {
        message.success(isEdit ? '更新成功' : '创建成功')
        setModalVisible(false)
        loadData()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch { message.error(isEdit ? '更新失败' : '创建失败') }
  }

  // ---- 删除 ----
  const handleDelete = async (id: number) => {
    const res = await purchaseApi.delete(id)
    if (res.code === 200) { message.success('删除成功'); loadData() }
    else message.error(res.message || '删除失败')
  }

  // ---- 审核（先弹出详情确认） ----
  const [auditLoading, setAuditLoading] = useState(false)
  const handleAuditClick = async (record: Purchase) => {
    setCurrentPurchase(record)
    // 加载完整详情（含商品明细）
    try {
      const res = await purchaseApi.get(record.id)
      if (res.code === 200) {
        const d = res.data
        setCurrentPurchase({
          ...record,
          items: safeArr<PurchaseItem>(d.items).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || item.product_name || '',
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price) || 0,
            total_price: Number(item.total_price) || 0,
            received_qty: item.received_qty
          }))
        })
        setAuditVisible(true)
      } else {
        message.error('加载订单详情失败')
      }
    } catch {
      message.error('加载订单详情失败')
    }
  }
  const handleAuditConfirm = async () => {
    if (!currentPurchase) return
    setAuditLoading(true)
    try {
      const res = await purchaseApi.audit(currentPurchase.id)
      if (res.code === 200) {
        message.success('审核通过')
        setAuditVisible(false)
        loadData()
      } else {
        message.error(res.message || '审核失败')
      }
    } catch {
      message.error('审核失败')
    } finally {
      setAuditLoading(false)
    }
  }

  // ---- 取消 ----
  const handleCancel = async (id: number) => {
    const res = await purchaseApi.cancel(id)
    if (res.code === 200) { message.success('取消成功'); loadData() }
    else message.error(res.message || '取消失败')
  }

  // ---- 详情 ----
  const handleDetail = async (record: Purchase) => {
    try {
      const res = await purchaseApi.get(record.id)
      if (res.code === 200) {
        const d = res.data
        setCurrentPurchase({
          ...record,
          supplier: d.supplier || record.supplier,
          warehouse: d.warehouse || record.warehouse,
          items: safeArr<PurchaseItem>(d.items).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || item.product_name || '',
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price) || 0,
            total_price: Number(item.total_price) || 0,
            received_qty: item.received_qty
          }))
        })
        setDetailVisible(true)

        // 已入库/已完成：加载入库记录
        if (d.status === 3 || d.status === 4) {
          fetch('/api/inventory/purchase-order/' + record.id + '/logs')
            .then(r => r.json())
            .then(r2 => {
              if (r2.code === 200) setStockInLogs(safeArr(r2.data?.list))
            })
            .catch(() => {})
        } else {
          setStockInLogs([])
        }
      } else {
        message.error('加载详情失败')
      }
    } catch {
      message.error('加载详情失败')
    }
  }

  // ---- 入库 ----
  const handleReceive = (record: Purchase) => {
    setCurrentPurchase(record)
    receiveForm.resetFields()
    setReceiveVisible(true)
  }
  const handleReceiveSubmit = async () => {
    try {
      const vals = await receiveForm.validateFields()
      const res = await purchaseApi.receive(currentPurchase!.id, vals)
      if (res.code === 200) { message.success('入库成功'); setReceiveVisible(false); loadData() }
      else message.error(res.message || '入库失败')
    } catch { message.error('入库失败') }
  }

  // ---- 付款 ----
  const handlePayClick = (record: Purchase) => {
    setCurrentPurchase(record)
    payForm.setFieldsValue({ amount: record.total_amount })
    setPayVisible(true)
  }
  const handlePaySubmit = async () => {
    if (!currentPurchase) return
    setPayLoading(true)
    try {
      const vals = await payForm.validateFields()
      const res = await purchaseApi.pay(currentPurchase.id, vals)
      if (res.code === 200) {
        message.success('付款成功，财务记录已生成')
        setPayVisible(false)
        loadData()
      } else {
        message.error(res.message || '付款失败')
      }
    } catch { message.error('付款失败') }
    finally { setPayLoading(false) }
  }

  // ---- 商品明细 ----
  const addItem = () => setItems(prev => [...prev, { product_id: 0, product_name: '', quantity: 1, unit_price: 0, total_price: 0 }])
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx))

  const updateItem = (idx: number, field: keyof PurchaseItem, rawVal: unknown) => {
    const val = rawVal as any
    setItems(prev => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: val }
      if (field === 'product_id') {
        const prod = products.find(p => p.id === val)
        if (prod) {
          next[idx].product_name = prod.name
          next[idx].unit_price = Number(prod.enterprise_price) || 0
        } else {
          next[idx].product_name = ''
          next[idx].unit_price = 0
        }
      }
      next[idx].total_price = Number(next[idx].quantity || 0) * Number(next[idx].unit_price || 0)
      return next
    })
  }

  const totalAmount = useMemo(() => items.reduce((s, it) => s + (it.total_price || 0), 0), [items])

  // ---- 表格列 ----
  const columns = [
    { title: '采购单号', dataIndex: 'order_no', key: 'order_no', width: 160 },
    { title: '供应商', dataIndex: 'supplier_name', key: 'supplier_name', width: 130,
      render: (_: any, r: Purchase) => r.supplier?.name || '-' },
    { title: '仓库', dataIndex: 'warehouse_name', key: 'warehouse_name', width: 110,
      render: (_: any, r: Purchase) => r.warehouse?.name || '-' },
    { title: '总金额', dataIndex: 'total_amount', key: 'total_amount', width: 110,
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    { title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (v: number) => <Tag color={STATUS_MAP[v]?.color}>{STATUS_MAP[v]?.text || v}</Tag> },
    { title: '预计到货', dataIndex: 'expected_date', key: 'expected_date', width: 110,
      render: (v: string) => v ? v.substring(0, 10) : '-' },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v: string) => v ? v.substring(0, 19).replace('T', ' ') : '-' },
    {
      title: '操作', key: 'action', width: 260,
      render: (_: any, record: Purchase) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleDetail(record)}>详情</Button>
          {record.status === 1 && <>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
            <Popconfirm title="确认删除?" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
            <Button type="link" size="small" style={{ color: '#52c41a' }} icon={<CheckCircleOutlined />} onClick={() => handleAuditClick(record)}>审核</Button>
          </>}
          {/* 入库功能已移至库存管理页面的待入库清单 - 2026-05-08 */}
          {(record.status === 3) && (
            <Button type="link" size="small" style={{ color: '#1890ff' }} icon={<PayCircleOutlined />} onClick={() => handlePayClick(record)}>付款</Button>
          )}
          {record.status === 1 &&
            <Popconfirm title="确认取消?" onConfirm={() => handleCancel(record.id)}>
              <Button type="link" size="small" danger>取消</Button>
            </Popconfirm>
          }
        </Space>
      )
    }
  ]

  // ---- 商品明细子表格列 ----
  const itemColumns = [
    { title: '商品', dataIndex: 'product_id', key: 'product_id', width: 280,
      render: (_: any, __: any, idx: number) => (
        <Select
          showSearch allowClear placeholder="搜索商品名称或编码"
          value={items[idx]?.product_id || undefined}
          onChange={val => updateItem(idx, 'product_id', val)}
          filterOption={(inp, opt) => {
            const prod = products.find(p => p.id === opt?.value)
            if (!prod) return false
            const low = inp.toLowerCase()
            return (prod.pinyin || '').toLowerCase().includes(low)
              || prod.name.toLowerCase().includes(low)
              || (prod.code || '').toLowerCase().includes(low)
          }}
        >
          {products.map(p => <Option key={p.id} value={p.id}>{p.name} (¥{Number(p.enterprise_price||0).toFixed(2)})</Option>)}
        </Select>
      )
    },
    { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 100,
      render: (_: any, __: any, idx: number) => (
        <InputNumber min={1} value={items[idx]?.quantity || 1} onChange={v => updateItem(idx, 'quantity', v)} style={{ width: '100%' }} />
      )
    },
    { title: '单价(¥)', dataIndex: 'unit_price', key: 'unit_price', width: 100,
      render: (_: any, __: any, idx: number) => (
        <InputNumber min={0} precision={2} value={items[idx]?.unit_price || 0}
          onChange={v => updateItem(idx, 'unit_price', v)} style={{ width: '100%' }} prefix="¥" />
      )
    },
    { title: '金额(¥)', dataIndex: 'total_price', key: 'total_price', width: 100,
      render: (v: number) => `¥${Number(v||0).toFixed(2)}` },
    { title: '操作', key: 'op', width: 60,
      render: (_: any, __: any, idx: number) => (
        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeItem(idx)} />
      )
    }
  ]

  // ---- 渲染 ----
  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* 搜索栏 */}
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="order_no"><Input placeholder="采购单号" style={{ width: 160 }} /></Form.Item>
          <Form.Item name="supplier_id">
            <Select placeholder="供应商" style={{ width: 150 }} allowClear>
              {suppliers.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" style={{ width: 110 }} allowClear>
              {Object.entries(STATUS_MAP).map(([k, v]) => <Option key={k} value={Number(k)}>{v.text}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增采购</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 主表 */}
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page, pageSize, total,
            showSizeChanger: true, showTotal: t => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={isEdit ? '编辑采购单' : '新增采购单'}
        open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)}
        width={920} okText={isEdit ? '保存' : '提交'} cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="supplier_id" label="供应商" rules={[{ required: true, message: '请选择供应商' }]}>
                <Select placeholder="选择供应商">
                  {suppliers.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="warehouse_id" label="收货仓库" rules={[{ required: true, message: '请选择仓库' }]}>
                <Select placeholder="选择仓库">
                  {warehouses.map(w => <Option key={w.id} value={w.id}>{w.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_date" label="预计到货日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={2} /></Form.Item>

          <Divider>采购商品明细</Divider>

          <Button type="dashed" icon={<PlusOutlined />} onClick={addItem} style={{ marginBottom: 8 }}>添加商品</Button>

          <Table
            dataSource={items} rowKey={(_, idx) => String(idx)} pagination={false} size="small"
            columns={itemColumns} scroll={{ x: 640 }}
          />

          {items.length === 0 && (
            <div style={{ textAlign: 'center', color: '#bbb', padding: '16px 0' }}>点击上方按钮添加商品</div>
          )}

          <div style={{ textAlign: 'right', marginTop: 12, fontSize: 16, fontWeight: 600, color: '#55266c' }}>
            总金额：¥{totalAmount.toFixed(2)}
          </div>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={`采购单详情 - ${currentPurchase?.order_no || ''}`}
        open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={900}
      >
        {currentPurchase && (
          <div>
            <Row gutter={[16, 8]}>
              <Col span={8}><strong>供应商：</strong>{currentPurchase.supplier?.name || '-'}</Col>
              <Col span={8}><strong>仓库：</strong>{currentPurchase.warehouse?.name || '-'}</Col>
              <Col span={8}><strong>总金额：</strong>¥{Number(currentPurchase.total_amount||0).toFixed(2)}</Col>
              <Col span={8}><strong>状态：</strong><Tag color={STATUS_MAP[currentPurchase.status]?.color}>{STATUS_MAP[currentPurchase.status]?.text}</Tag></Col>
              <Col span={8}><strong>预计到货：</strong>{currentPurchase.expected_date ? currentPurchase.expected_date.substring(0,10) : '-'}</Col>
              <Col span={24}><strong>备注：</strong>{currentPurchase.remark || '-'}</Col>
            </Row>
            <Divider>采购明细</Divider>
            <Table
              columns={[
                { title: '商品', dataIndex: 'product_name', key: 'product_name' },
                { title: '数量', dataIndex: 'quantity', key: 'quantity' },
                { title: '单价', dataIndex: 'unit_price', key: 'unit_price', render: (v: number) => `¥${Number(v||0).toFixed(2)}` },
                { title: '金额', dataIndex: 'total_price', key: 'total_price', render: (v: number) => `¥${Number(v||0).toFixed(2)}` },
                { title: '已入库', dataIndex: 'received_qty', key: 'received_qty' }
              ]}
              dataSource={currentPurchase.items || []}
              rowKey="id" size="small" pagination={false}
            />

            {/* 入库记录 - 仅已入库/已完成状态显示 */}
            {(currentPurchase?.status === 3 || currentPurchase?.status === 4) && (
              <>
                <Divider>入库记录</Divider>
                {stockInLogs.length === 0 ? (
                  <div style={{ color: '#999', textAlign: 'center', padding: '12px 0' }}>暂无入库记录</div>
                ) : (
                  <Table
                    columns={[
                      { title: '商品', dataIndex: ['product', 'name'], key: 'product_name', render: (_: any, r: any) => r.product?.name || '-' },
                      { title: '仓库', dataIndex: ['warehouse', 'name'], key: 'warehouse_name', render: (_: any, r: any) => r.warehouse?.name || '-' },
                      { title: '批次号', dataIndex: 'batch_no', key: 'batch_no' },
                      { title: '入库数量', dataIndex: 'quantity', key: 'quantity', render: (v: number) => v },
                      { title: '单价(¥)', dataIndex: 'unit_price', key: 'unit_price', render: (v: number) => `¥${Number(v||0).toFixed(2)}` },
                      { title: '入库时间', dataIndex: 'created_at', key: 'created_at', render: (v: string) => v ? v.substring(0, 19).replace('T', ' ') : '-' }
                    ]}
                    dataSource={stockInLogs}
                    rowKey="id" size="small" pagination={false}
                  />
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* 审核确认弹窗 */}
      <Modal
        title={
          <span>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            审核确认 - {currentPurchase?.order_no || ''}
          </span>
        }
        open={auditVisible}
        onCancel={() => setAuditVisible(false)}
        onOk={handleAuditConfirm}
        okText="确认审核通过"
        cancelText="拒绝并关闭"
        okButtonProps={{ loading: auditLoading }}
        width={900}
      >
        {currentPurchase && (
          <div>
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: 8 }}>📋 采购订单信息</div>
              <Row gutter={[24, 8]}>
                <Col span={8}><span style={{ color: '#666' }}>采购单号：</span><strong>{currentPurchase.order_no}</strong></Col>
                <Col span={8}><span style={{ color: '#666' }}>供应商：</span><strong>{currentPurchase.supplier?.name || '-'}</strong></Col>
                <Col span={8}><span style={{ color: '#666' }}>收货仓库：</span><strong>{currentPurchase.warehouse?.name || '-'}</strong></Col>
                <Col span={8}><span style={{ color: '#666' }}>创建时间：</span><strong>{currentPurchase.created_at ? currentPurchase.created_at.substring(0, 19).replace('T', ' ') : '-'}</strong></Col>
                <Col span={8}><span style={{ color: '#666' }}>预计到货：</span><strong>{currentPurchase.expected_date ? currentPurchase.expected_date.substring(0, 10) : '-'}</strong></Col>
                <Col span={8}><span style={{ color: '#666' }}>备注：</span><strong>{currentPurchase.remark || '无'}</strong></Col>
              </Row>
            </div>

            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>🛒 采购商品明细</div>
            <Table
              columns={[
                { title: '序号', key: 'idx', width: 60, render: (_: any, __: any, idx: number) => idx + 1 },
                { title: '商品名称', dataIndex: 'product_name', key: 'product_name' },
                { title: '采购数量', dataIndex: 'quantity', key: 'quantity', width: 100, align: 'center', render: (v: number) => <strong>{v}</strong> },
                { title: '采购单价(¥)', dataIndex: 'unit_price', key: 'unit_price', width: 120, align: 'right', render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
                { title: '采购金额(¥)', dataIndex: 'total_price', key: 'total_price', width: 120, align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: '#cf1322' }}>¥${Number(v || 0).toFixed(2)}</span> }
              ]}
              dataSource={currentPurchase.items || []}
              rowKey="id" size="small" pagination={false}
            />
            {currentPurchase.items && currentPurchase.items.length > 0 && (
              <div style={{ textAlign: 'right', marginTop: 12, fontSize: 16, fontWeight: 700, color: '#cf1322' }}>
                合计金额：¥{currentPurchase.items.reduce((s, it) => s + (it.total_price || 0), 0).toFixed(2)}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 入库弹窗 */}
      <Modal title="采购入库" open={receiveVisible} onOk={handleReceiveSubmit} onCancel={() => setReceiveVisible(false)}>
        <Form form={receiveForm} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
          <Form.Item name="product_id" label="商品ID" rules={[{ required: true, message: '请输入商品ID' }]}>
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="quantity" label="入库数量" rules={[{ required: true, message: '请输入数量' }]}>
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="batch_no" label="批次号"><Input /></Form.Item>
          <Form.Item name="remark" label="备注"><Input /></Form.Item>
        </Form>
      </Modal>

      {/* 付款弹窗 */}
      <Modal
        title={<span><PayCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />采购付款 - {currentPurchase?.order_no || ''}</span>}
        open={payVisible}
        onOk={handlePaySubmit}
        onCancel={() => setPayVisible(false)}
        okText="确认付款"
        okButtonProps={{ loading: payLoading }}
        width={500}
      >
        {currentPurchase && (
          <div>
            <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 6, padding: '12px 16px', marginBottom: 16 }}>
              <Row gutter={[12, 8]}>
                <Col span={12}><span style={{ color: '#666' }}>供应商：</span><strong>{currentPurchase.supplier?.name || '-'}</strong></Col>
                <Col span={12}><span style={{ color: '#666' }}>采购金额：</span><strong style={{ color: '#cf1322' }}>¥{Number(currentPurchase.total_amount||0).toFixed(2)}</strong></Col>
              </Row>
            </div>
            <Form form={payForm} layout="vertical">
              <Form.Item name="amount" label="付款金额" rules={[{ required: true, message: '请输入付款金额' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} prefix="¥" />
              </Form.Item>
              <Form.Item name="bank_account_id" label="付款账户">
                <Input placeholder="如：工商银行尾号1234" />
              </Form.Item>
              <Form.Item name="remark" label="备注">
                <Input.TextArea rows={2} placeholder="付款备注（可选）" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  )
}
